/**
 * SemanticZoomOverlay — Interaktive Darstellung des Semantic-Zoom-Konzepts
 *
 * Zeigt die aktuelle Zoom-Stufe, Layer-Visibility-Matrix und eine
 * visuelle Vorschau aller vier Stufen. Wird per Klick auf den
 * Zoom-Tier-Indikator geöffnet.
 *
 * Styles: ./SemanticZoomOverlay.css
 * Alle statischen Styles sind ausgelagert; inline bleiben nur
 * echte Laufzeitwerte (berechnete Tier-Farben, aktiver Zustand).
 */

import React, { useMemo, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react'
import './SemanticZoomOverlay.css'
import type { ZoomTier, LayerState } from '../hooks/useSemanticZoom'

// ── Konstanten ────────────────────────────────────────────────────────────

const TIER_COLORS: Record<ZoomTier, string> = {
  overview: '#6366f1',
  context:  '#f59e0b',
  detail:   '#10b981',
  inspect:  '#ef4444',
}

const TIER_LABELS: Record<ZoomTier, string> = {
  overview: 'Übersicht',
  context:  'Kontext',
  detail:   'Detail',
  inspect:  'Inspektion',
}

const TIER_ANALOGIES: Record<ZoomTier, string> = {
  overview: 'Stadtplan-Ansicht',
  context:  'Straßenkarten-Ansicht',
  detail:   'Gebäudeplan-Ansicht',
  inspect:  'Raumdetail-Ansicht',
}

const TIER_RANGES: Record<ZoomTier, string> = {
  overview: '> 80 m',
  context:  '20–80 m',
  detail:   '5–20 m',
  inspect:  '< 5 m',
}

const LAYER_DISPLAY_NAMES: Record<string, string> = {
  floorLabels:      'Geschoss-Labels',
  floorLines:       'Geschoss-Linien',
  sectionLines:     'Schnitt-Linien',
  annotations:      'Annotationen',
  annotationPopups: 'Annotation-Popups',
  measurements:     'Messungen',
  navCube:          'Navigation Cube',
  boundingBox:      'Bounding Box',
  cloudMetadata:    'Metadaten',
}

const LAYER_ICONS: Record<string, string> = {
  floorLabels:      '▬',
  floorLines:       '═',
  sectionLines:     '╱',
  annotations:      '◉',
  annotationPopups: '◫',
  measurements:     '📐',
  navCube:          '⬡',
  boundingBox:      '▢',
  cloudMetadata:    'ⓘ',
}

const FULL_LAYER_MATRIX: Record<string, Record<ZoomTier, { show: boolean; [k: string]: any }>> = {
  floorLabels: {
    overview: { show: true,  opacity: 0.6, connector: false },
    context:  { show: true,  opacity: 0.9, connector: true },
    detail:   { show: true,  opacity: 1.0, connector: true },
    inspect:  { show: false },
  },
  floorLines: {
    overview: { show: true,  opacity: 0.15 },
    context:  { show: true,  opacity: 0.35 },
    detail:   { show: true,  opacity: 0.5 },
    inspect:  { show: false },
  },
  sectionLines: {
    overview: { show: false },
    context:  { show: true,  opacity: 0.4 },
    detail:   { show: true,  opacity: 0.7 },
    inspect:  { show: true,  opacity: 1.0 },
  },
  annotations: {
    overview: { show: true,  style: 'cluster', dotSize: 6, label: false },
    context:  { show: true,  style: 'dot',     dotSize: 10, label: false },
    detail:   { show: true,  style: 'dot+label', dotSize: 12, label: true },
    inspect:  { show: true,  style: 'dot+label', dotSize: 14, label: true },
  },
  annotationPopups: {
    overview: { show: false },
    context:  { show: false },
    detail:   { show: true,  variant: 'compact' },
    inspect:  { show: true,  variant: 'expanded' },
  },
  measurements: {
    overview: { show: false },
    context:  { show: true,  labels: false },
    detail:   { show: true,  labels: true },
    inspect:  { show: true,  labels: true, handles: true },
  },
  navCube: {
    overview: { show: true },
    context:  { show: true },
    detail:   { show: true },
    inspect:  { show: false },
  },
  boundingBox: {
    overview: { show: true,  opacity: 0.15 },
    context:  { show: true,  opacity: 0.08 },
    detail:   { show: false },
    inspect:  { show: false },
  },
  cloudMetadata: {
    overview: { show: true },
    context:  { show: false },
    detail:   { show: false },
    inspect:  { show: true },
  },
}

const ALL_TIERS: ZoomTier[] = ['overview', 'context', 'detail', 'inspect']

// ── Props ─────────────────────────────────────────────────────────────────

interface SemanticZoomOverlayProps {
  tier: ZoomTier
  distance: number
  layerState: LayerState
  onClose: () => void
}

// Öffentliche API für externe Steuerung (z.B. ZTI-Button)
export interface SemanticZoomOverlayHandle {
  close: () => void
}

// ── Component ─────────────────────────────────────────────────────────────

export const SemanticZoomOverlay = forwardRef<SemanticZoomOverlayHandle, SemanticZoomOverlayProps>(
function SemanticZoomOverlay({
  tier,
  distance,
  layerState,
  onClose,
}, ref) {
  // 'entering' → ein rAF → 'visible' (slide in)
  // close() → 'closing' (slide out) → onTransitionEnd → onClose()
  const [phase, setPhase] = useState<'entering' | 'visible' | 'closing'>('entering')

  useEffect(() => {
    const id = requestAnimationFrame(() => setPhase('visible'))
    return () => cancelAnimationFrame(id)
  }, [])

  const handleClose = useCallback(() => {
    setPhase('closing')
  }, [])

  // Exponiert close() nach außen (z.B. für ZTI-Button)
  useImperativeHandle(ref, () => ({ close: handleClose }), [handleClose])

  const handleTransitionEnd = useCallback((e: React.TransitionEvent) => {
    if (e.propertyName === 'transform' && phase === 'closing') {
      onClose()
    }
  }, [phase, onClose])

  return (
    <div
      data-component="SemanticZoomOverlay"
      className="szo"
      data-phase={phase}
      onTransitionEnd={handleTransitionEnd}
    >
      <div>
        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="szo__header">
          <div>
            <h2 className="szo__title">Semantic Zoom</h2>
            <p className="szo__subtitle">
              Distanzabhängige Informationsvisualisierung
            </p>
          </div>
          <button className="szo__close-btn" onClick={handleClose}>
            ✕
          </button>
        </div>

        {/* ── Distanz + aktive Stufe ──────────────────────────────── */}
        <div className="szo__distance-section">
          <div className="szo__meta-row">
            <div>
              <div className="szo__cam-label">Kamera-Distanz</div>
              <div className="szo__cam-value">{distance.toFixed(1)} m</div>
            </div>

            {/* Tier-Badge: data-tier liefert Custom Properties */}
            <div className="szo__tier-badge" data-tier={tier}>
              {TIER_LABELS[tier]}
            </div>

            <span className="szo__analogy">{TIER_ANALOGIES[tier]}</span>
          </div>

          {/* Tier-Leiste */}
          <div className="szo__tier-bar">
            {ALL_TIERS.map(t => (
              <div
                key={t}
                className="szo__tier-bar-item"
                data-tier={t}
                data-active={String(t === tier)}
              >
                <div className="szo__tier-track" />
                <span className="szo__tier-range">{TIER_RANGES[t]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Layer-Matrix ─────────────────────────────────────────── */}
        <div className="szo__section">
          <div className="szo__section-label">Layer-Visibility-Matrix</div>

          {/* Spalten-Header */}
          <div className="szo__matrix-head">
            <div className="szo__matrix-spacer" />
            {ALL_TIERS.map(t => (
              <div
                key={t}
                className="szo__matrix-th"
                data-tier={t}
                data-active-tier={String(t === tier)}
              >
                {TIER_LABELS[t]}
              </div>
            ))}
          </div>

          {/* Daten-Zeilen */}
          {Object.entries(FULL_LAYER_MATRIX).map(([layerName, matrix]) => (
            <div key={layerName} className="szo__matrix-row">

              {/* Layer-Name */}
              <div className="szo__matrix-name">
                <span className="szo__matrix-icon">{LAYER_ICONS[layerName]}</span>
                {LAYER_DISPLAY_NAMES[layerName]}
              </div>

              {/* Zellen pro Tier-Spalte */}
              {ALL_TIERS.map(t => {
                const show = matrix[t]?.show ?? false
                return (
                  <div
                    key={t}
                    className="szo__matrix-cell"
                    data-tier={t}
                    data-active-tier={String(t === tier)}
                    data-show={String(show)}
                  >
                    {show
                      ? <div className="szo__matrix-dot" />
                      : <span className="szo__matrix-empty">—</span>
                    }
                  </div>
                )
              })}
            </div>
          ))}
        </div>

        {/* ── Visuelle Stufen-Vorschau ─────────────────────────────── */}
        <div className="szo__preview-section">
          <div className="szo__section-label">Stufen-Vorschau</div>
          <div className="szo__preview-grid">
            {ALL_TIERS.map(t => (
              <TierPreviewCard key={t} previewTier={t} activeTier={tier} />
            ))}
          </div>
        </div>

        {/* ── Details zum aktiven Layer ────────────────────────────── */}
        <div className="szo__details-section">
          <div className="szo__section-label">
            Aktive Konfiguration — {TIER_LABELS[tier]}
          </div>
          <div className="szo__details-grid">
            {Object.entries(FULL_LAYER_MATRIX).map(([layerName, matrix]) => {
              const cfg = matrix[tier]
              const show = cfg?.show ?? false
              return (
                <div
                  key={layerName}
                  className="szo__detail-card"
                  data-show={String(show)}
                >
                  <div className="szo__detail-card-header">
                    <span className="szo__detail-icon">{LAYER_ICONS[layerName]}</span>
                    <span className="szo__detail-name">
                      {LAYER_DISPLAY_NAMES[layerName]}
                    </span>
                    <span className="szo__detail-badge">
                      {show ? 'an' : 'aus'}
                    </span>
                  </div>
                  {show && (
                    <div className="szo__detail-config">
                      {Object.entries(cfg)
                        .filter(([k]) => k !== 'show')
                        .map(([k, v]) => (
                          <span key={k} className="szo__config-param">
                            <span className="szo__config-key">{k}:</span>{' '}
                            <span className="szo__config-val">{String(v)}</span>
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
})

// ── Sub: Stufen-Vorschau-Karte ────────────────────────────────────────────

function TierPreviewCard({
  previewTier,
  activeTier,
}: {
  previewTier: ZoomTier
  activeTier: ZoomTier
}) {
  const isActive = previewTier === activeTier

  const descriptions: Record<ZoomTier, string[]> = {
    overview: ['Gebäudeumriss', 'Cluster-Marker', 'Geschoss-Ticks'],
    context:  ['Einzelmarker', 'Geschoss-Labels', 'Schnittlinien', 'Messlinien'],
    detail:   ['Marker + Labels', 'Messwerte', 'Tooltips', 'Geschoss-Connectors'],
    inspect:  ['Popups + Coords', 'Tags', 'Handles', 'Inline-Editor'],
  }

  return (
    <div
      className="szo__preview-card"
      data-tier={previewTier}
      data-active={String(isActive)}
    >
      {/* Header */}
      <div className="szo__preview-header">
        <div className="szo__preview-dot" />
        <span className="szo__preview-title">{TIER_LABELS[previewTier]}</span>
        <span className="szo__preview-range">{TIER_RANGES[previewTier]}</span>
      </div>

      {/* Mockup SVG */}
      <div className="szo__preview-svg-wrapper">
        <svg viewBox="0 0 160 100" className="szo__preview-svg">
          {Array.from({ length: 30 }, (_, i) => (
            <circle key={i}
              cx={30 + Math.sin(i * 1.7) * 50 + 20}
              cy={15 + Math.cos(i * 2.3) * 35 + 20}
              r={0.6 + (i % 3) * 0.4}
              fill={`hsl(${20 + (i % 5) * 8}, ${25 + (i % 3) * 15}%, ${28 + (i % 4) * 8}%)`}
              opacity={0.3 + (i % 3) * 0.15}
            />
          ))}
          {previewTier === 'overview' && <OverviewMockup tier={previewTier} />}
          {previewTier === 'context'  && <ContextMockup  tier={previewTier} />}
          {previewTier === 'detail'   && <DetailMockup   tier={previewTier} />}
          {previewTier === 'inspect'  && <InspectMockup  tier={previewTier} />}
        </svg>
      </div>

      {/* Feature-Liste */}
      <div className="szo__preview-features">
        {descriptions[previewTier].map((desc, i) => (
          <div key={i} className="szo__preview-feature">
            <span className="szo__preview-bullet">●</span>
            {desc}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Mockup SVGs ───────────────────────────────────────────────────────────
// Die Farben werden direkt als TIER_COLORS[tier] übergeben, da SVG-Attribute
// keine CSS Custom Properties unterstützen.

function OverviewMockup({ tier }: { tier: ZoomTier }) {
  const color = TIER_COLORS[tier]
  return (
    <g>
      <rect x="40" y="15" width="65" height="70" rx="2" fill="none" stroke="#555" strokeWidth="0.8" strokeDasharray="3,2" />
      <circle cx="60" cy="40" r="10" fill={color + '33'} stroke={color} strokeWidth="1.2" />
      <text x="60" y="44" textAnchor="middle" fill={color} fontSize="9" fontWeight="700">5</text>
      <circle cx="90" cy="60" r="7" fill="#e6383833" stroke="#e63838" strokeWidth="1" />
      <text x="90" y="63" textAnchor="middle" fill="#e63838" fontSize="7" fontWeight="700">3</text>
      {[25, 40, 55, 70].map(y => (
        <line key={y} x1="105" y1={y} x2="112" y2={y} stroke="#ff880055" strokeWidth="0.6" />
      ))}
    </g>
  )
}

function ContextMockup({ tier }: { tier: ZoomTier }) {
  const color = TIER_COLORS[tier]
  return (
    <g>
      <circle cx="55" cy="35" r="4" fill="#4a8fe8" stroke="#fff" strokeWidth="1.2" />
      <circle cx="80" cy="50" r="4" fill="#e63232" stroke="#fff" strokeWidth="1.2" />
      <circle cx="65" cy="68" r="4" fill="#4ae88a" stroke="#fff" strokeWidth="1.2" />
      {[25, 45, 65].map((y, i) => (
        <g key={y}>
          <line x1="108" y1={y} x2="120" y2={y} stroke="#ff880088" strokeWidth="0.6" />
          <rect x="121" y={y - 5} width="22" height="10" rx="2.5" fill="#ff8800cc" />
          <text x="132" y={y + 2} textAnchor="middle" fill="#fff" fontSize="5.5" fontWeight="700">
            {['3OG', '2OG', '1OG'][i]}
          </text>
        </g>
      ))}
      <line x1="35" y1="55" x2="100" y2="53" stroke="#1a73e8" strokeWidth="0.8" opacity="0.4" />
    </g>
  )
}

function DetailMockup({ tier }: { tier: ZoomTier }) {
  const color = TIER_COLORS[tier]
  return (
    <g>
      <circle cx="50" cy="38" r="5" fill="#4a8fe8" stroke="#fff" strokeWidth="1.2" />
      <rect x="58" y="32" width="38" height="11" rx="2.5" fill="rgba(0,0,0,0.7)" />
      <text x="77" y="40" textAnchor="middle" fill="#fff" fontSize="5.5" fontWeight="600">Riss Decke</text>
      <circle cx="70" cy="65" r="5" fill="#e63232" stroke="#fff" strokeWidth="1.2" />
      <rect x="78" y="59" width="30" height="11" rx="2.5" fill="rgba(0,0,0,0.7)" />
      <text x="93" y="67" textAnchor="middle" fill="#fff" fontSize="5.5" fontWeight="600">Asbest</text>
      <line x1="35" y1="85" x2="110" y2="85" stroke="#2eb88a" strokeWidth="1" />
      <line x1="35" y1="82" x2="35" y2="88" stroke="#2eb88a" strokeWidth="0.8" />
      <line x1="110" y1="82" x2="110" y2="88" stroke="#2eb88a" strokeWidth="0.8" />
      <rect x="60" y="79" width="22" height="8" rx="1.5" fill="#111" />
      <text x="71" y="85" textAnchor="middle" fill="#2eb88a" fontSize="5" fontWeight="600">4.82m</text>
    </g>
  )
}

function InspectMockup({ tier }: { tier: ZoomTier }) {
  const color = TIER_COLORS[tier]
  return (
    <g>
      <circle cx="45" cy="22" r="5.5" fill="#e63232" stroke="#fff" strokeWidth="1.5" />
      <rect x="20" y="30" width="100" height="58" rx="4" fill="#fff" stroke="#e5e7eb" strokeWidth="0.6" />
      <rect x="20" y="30" width="100" height="13" rx="4" fill="#fafafa" />
      <text x="28" y="39" fill="#9ca3af" fontSize="4.5" fontFamily="monospace">(3.2, -1.5, 12.4)</text>
      <rect x="28" y="48" width="30" height="8" rx="2.5" fill="#e6323218" />
      <text x="43" y="54" textAnchor="middle" fill="#e63232" fontSize="4.5" fontWeight="700">SCHADSTOFF</text>
      <text x="28" y="66" fill="#111" fontSize="6.5" fontWeight="700">Asbest (A3)</text>
      <rect x="28" y="72" width="18" height="7" rx="1.5" fill="#f3f4f6" />
      <text x="37" y="77" textAnchor="middle" fill="#9ca3af" fontSize="3.5">#labor</text>
      <rect x="49" y="72" width="22" height="7" rx="1.5" fill="#f3f4f6" />
      <text x="60" y="77" textAnchor="middle" fill="#9ca3af" fontSize="3.5">#dringend</text>
    </g>
  )
}
